import { Check } from '@phosphor-icons/react';
import type { Manner, StockVoiceDto } from '@app/shared';
import { cn } from '../lib/cn';
import { renderApi } from '../lib/render-client';
import { haptic } from '../lib/tma';
import { SampleButton, type SamplePlayer } from './SampleButton';

/**
 * The ready-made voices (spec 5.2): ▶ plays the sample phrase in the chosen
 * manner, then the name, two words about the sound and the mark of the choice.
 * A voice whose sample file is not made yet has no ▶.
 */
export function StockVoiceList({
  voices,
  chosen,
  manner,
  player,
  disabled,
  onChoose,
}: {
  voices: StockVoiceDto[];
  chosen: string | null;
  manner: Manner;
  player: SamplePlayer;
  disabled: boolean;
  onChoose: (id: string) => void;
}) {
  return (
    <ul className="rounded-2xl bg-surface shadow-card divide-y divide-line overflow-hidden">
      {voices.map((v) => {
        const sample = v.samples[manner];
        const on = v.id === chosen;
        return (
          <li key={v.id} className="flex items-center gap-3 px-3 py-2.5">
            {sample ? (
              <SampleButton player={player} sampleKey={`stock:${v.id}:${manner}`} source={renderApi.absoluteUrl(sample)} />
            ) : (
              <span className="w-9 flex-shrink-0" aria-hidden />
            )}
            <button
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => {
                if (on) return;
                haptic('light');
                onChoose(v.id);
              }}
              className="flex flex-1 min-w-0 items-center gap-2 text-left disabled:opacity-60"
            >
              <span className="min-w-0 flex-1">
                <span className={cn('block text-sm leading-tight', on ? 'font-bold' : 'font-medium')}>{v.name}</span>
                <span className="block text-xs text-hint leading-snug break-words">{v.about}</span>
              </span>
              <span
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
                  on ? 'bg-accent text-accent-on' : 'bg-surface-2',
                )}
              >
                {on && <Check size={14} weight="bold" />}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
